import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { gql, useMutation, useQuery } from "@apollo/client";
import { PERMISSIONS } from "../Permissions/Permissions";
import { toast } from "react-toastify";
import { GetPermissionQuery } from "../../gql/graphql";

const GET = gql`
  query GetPermission($permissionId: UUID!, $userId: UUID!) {
    users(where: { id: { eq: $userId } }) {
      items {
        id
        email
        firstName
        lastName
      }
    }
    permissions(where: { id: { eq: $permissionId } }) {
      items {
        id
        userId
        siteId
        companyId
        areaId
        role
      }
    }
    companies(where: { isActive: { eq: true } }) {
      items {
        id
        name
        isActive #need to select values in where clause
      }
    }
    sites(where: { isActive: { eq: true } }) {
      items {
        id
        companyId
        name
        isActive #need to select values in where clause
      }
    }
    areas {
      items {
        id
        name
        siteId
        site {
          id
          name
        }
      }
    }
    myPermissions {
      id
      companyId
      siteId
      areaId
      role
    }
  }
`;

const roles = ["Read", "Write", "Admin"];
const roleHierarchy: Record<string, number> = {
  Read: 1,
  Write: 2,
  Admin: 3,
  Parkagility: 4,
};

const UPDATE = gql`
  mutation UpdatePermission($permission: PermissionInput!) {
    updatePermission(permission: $permission) {
      id
      userId
      siteId
      companyId
      areaId
      role
    }
  }
`;

type UserType = NonNullable<
  NonNullable<GetPermissionQuery["users"]>["items"]
>[number];
type PermissionType = NonNullable<
  NonNullable<GetPermissionQuery["permissions"]>["items"]
>[number];

export const Permission = () => {
  const { id: userId, permissionId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserType | null>(null);
  const [permission, setPermission] = useState<PermissionType | null>(null);
  const [company, setCompany] = useState<string | null>(null);
  const [site, setSite] = useState<string | null>(null);
  const [area, setArea] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);

  const { data } = useQuery<GetPermissionQuery>(GET, {
    variables: {
      permissionId:
        permissionId === "new"
          ? "00000000-0000-0000-0000-000000000000"
          : permissionId,
      userId: userId,
    },
    fetchPolicy: "network-only",
  });

  const [updatePermission, { loading: updating }] = useMutation(UPDATE, {
    refetchQueries: [{ query: PERMISSIONS, variables: { userId: userId } }],
    awaitRefetchQueries: true,
  }); // [ updateCompany, { data, loading, error }]

  useEffect(() => {
    if (permissionId === "new") {
      setPermission({
        id: "00000000-0000-0000-0000-000000000000",
        companyId: null,
        siteId: null,
        areaId: null,
        role: null,
      });
    }
    if (data?.permissions?.items?.length === 1) {
      const permission = data.permissions.items[0];
      setPermission(permission);
      setCompany(permission?.companyId ?? null);
      setSite(permission?.siteId ?? null);
      setArea(permission?.areaId ?? null);
      setRole(permission?.role ?? null);
    }
    if (data?.users?.items?.length === 1) {
      setUser(data.users?.items[0]);
    }
  }, [data, permissionId]);

  // Get current user's permissions from the query
  const myPermissions = useMemo(() => data?.myPermissions ?? [], [data?.myPermissions]);
  const isSuperUser = useMemo(() => myPermissions.some((p) => p?.role === "SuperUser" || p?.role === "Parkagility"), [myPermissions]);

  // Helper to get max role level user can grant for a given scope
  const getMaxGrantableRoleLevel = useMemo(() => {
    return (targetCompanyId: string | null, targetSiteId: string | null, targetAreaId: string | null): number => {
      if (isSuperUser) return 4; // Can grant anything

      let maxLevel = 0;

      for (const perm of myPermissions) {
        if (!perm?.role || !(perm.role in roleHierarchy)) continue;
        const permLevel = roleHierarchy[perm.role];

        // Company-level permission: can grant to anything in that company
        if (perm.companyId && !perm.siteId && !perm.areaId) {
          if (targetCompanyId === perm.companyId) {
            maxLevel = Math.max(maxLevel, permLevel);
          }
        }

        // Site-level permission: can grant to that site or areas within it
        if (perm.siteId && !perm.areaId) {
          // Can only grant site or area level, not company level
          if (targetSiteId === perm.siteId && (targetSiteId || targetAreaId)) {
            maxLevel = Math.max(maxLevel, permLevel);
          }
        }

        // Area-level permission: can only grant to that specific area
        if (perm.areaId) {
          if (targetAreaId === perm.areaId) {
            maxLevel = Math.max(maxLevel, permLevel);
          }
        }
      }

      return maxLevel;
    };
  }, [myPermissions, isSuperUser]);

  // Check if user can grant a permission at a given scope
  const canGrantToCompany = (companyId: string): boolean => {
    if (isSuperUser) return true;
    return getMaxGrantableRoleLevel(companyId, null, null) > 0;
  };

  const canGrantToSite = (siteId: string, companyId: string): boolean => {
    if (isSuperUser) return true;
    // Check if user has company-level permission or site-level permission
    return getMaxGrantableRoleLevel(companyId, siteId, null) > 0;
  };

  const canGrantToArea = (areaId: string, siteId: string, companyId: string): boolean => {
    if (isSuperUser) return true;
    return getMaxGrantableRoleLevel(companyId, siteId, areaId) > 0;
  };

  // Get the company ID for a site
  const getCompanyIdForSite = (siteId: string): string | null => {
    const siteData = data?.sites?.items?.find((s) => s?.id === siteId);
    return siteData?.companyId ?? null;
  };

  // Get the site ID and company ID for an area
  const getSiteAndCompanyForArea = (areaId: string): { siteId: string | null; companyId: string | null } => {
    const areaData = data?.areas?.items?.find((a) => a?.id === areaId);
    if (!areaData?.siteId) return { siteId: null, companyId: null };
    const companyId = getCompanyIdForSite(areaData.siteId);
    return { siteId: areaData.siteId, companyId };
  };

  // Check if a role can be granted for the current selection
  const canGrantRole = (roleToGrant: string): boolean => {
    if (isSuperUser) return true;
    if (!(roleToGrant in roleHierarchy)) return false;

    const roleLevel = roleHierarchy[roleToGrant];

    // Determine the scope based on current selection
    let targetCompanyId = company;
    let targetSiteId = site;
    let targetAreaId = area;

    // If site is selected but company isn't explicitly set, find the company
    if (targetSiteId && !targetCompanyId) {
      targetCompanyId = getCompanyIdForSite(targetSiteId);
    }

    // If area is selected, find site and company
    if (targetAreaId) {
      const { siteId: areaSiteId, companyId: areaCompanyId } = getSiteAndCompanyForArea(targetAreaId);
      if (!targetSiteId) targetSiteId = areaSiteId;
      if (!targetCompanyId) targetCompanyId = areaCompanyId;
    }

    const maxLevel = getMaxGrantableRoleLevel(targetCompanyId, targetSiteId, targetAreaId);
    return roleLevel <= maxLevel;
  };

  let isValid = (company && role) || role === "SuperUser" || role === "";

  const {
    handleSubmit,
    formState: { errors },
  } = useForm();


  const onSubmit = () => {
    var permsToSet = {
      id: permission?.id,
      userId: user?.id,
      companyId: company,
      siteId: site,
      areaId: area,
      role: role,
    };

    updatePermission({ variables: { permission: permsToSet } }).then((x) => {
      if (x.data.updatePermission) {
        toast(`${user?.email} permission saved`, {
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
        });
        navigate(`/users/${user?.id}/permissions`);
      }
    });
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">
          Permissions - {user?.email} ({user?.firstName + " " + user?.lastName})
        </h1>
      </div>

      <div className="text-info mb-5">
        To add a permission for a user, choose either a Company, Site or Area,
        then the role you'd like to grant.
      </div>

      {permission && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={
            "needs-validation" +
            (Object.keys(errors).length > 0 ? " was-validated" : "")
          }
          noValidate
        >
          <div className="row mb-3">
            <div className="col-sm-2">Company</div>
            <div className="col-sm-10">
              <select
                className="form-select"
                value={company || ""}
                onChange={(e) => setCompany(e.target.value)}
              >
                <option value="">Select a company</option>
                {data?.companies?.items?.map((c) => {
                  const canGrant = canGrantToCompany(c?.id ?? "");
                  return (
                    <option key={c?.id} value={c?.id} disabled={!canGrant}>
                      {c?.name}{!canGrant ? " (no access)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-2">Site</div>
            <div className="col-sm-10">
              <select
                className="form-select"
                value={site || ""}
                onChange={(e) => setSite(e.target.value)}
                disabled={!company}
              >
                <option value="">
                  {company ? "(all sites)" : "Choose a company first"}
                </option>
                {company &&
                  data?.sites?.items
                    ?.filter((s) => s?.companyId === company)
                    .map((s) => {
                      const canGrant = canGrantToSite(s?.id ?? "", company);
                      return (
                        <option key={s?.id} value={s?.id} disabled={!canGrant}>
                          {s?.name}{!canGrant ? " (no access)" : ""}
                        </option>
                      );
                    })}
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-2">Area</div>
            <div className="col-sm-10">
              <select
                className="form-select"
                value={area || ""}
                onChange={(e) => setArea(e.target.value)}
                disabled={!site}
              >
                <option value="">
                  {site ? "(all areas)" : "Choose a site first"}
                </option>
                {site &&
                  data?.areas?.items
                    ?.filter((a) => a?.siteId === site)
                    .map((a) => {
                      const canGrant = canGrantToArea(a?.id ?? "", site, company ?? "");
                      return (
                        <option key={a?.id} value={a?.id} disabled={!canGrant}>
                          {a?.name}{!canGrant ? " (no access)" : ""}
                        </option>
                      );
                    })}
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-2">Role</div>
            <div className="col-sm-10">
              {roles.map((r, index) => {
                const canGrant = canGrantRole(r);
                return (
                  <span
                    key={index}
                    className={`btn ${
                      role === r ? "btn-secondary" : "btn-outline-secondary"
                    } me-3 ${!canGrant ? "disabled" : ""}`}
                    onClick={() => canGrant && setRole(r)}
                    style={!canGrant ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    title={!canGrant ? "You don't have permission to grant this role" : ""}
                  >
                    {r}
                  </span>
                );
              })}

              <span
                className={`btn ${role === "SuperUser" ? "btn-danger" : "btn-outline-danger"
                } mr-3 ${!isSuperUser ? "disabled" : ""}`}
                onClick={() => isSuperUser && setRole("SuperUser")}
                style={!isSuperUser ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                title={!isSuperUser ? "Only SuperUser users can grant SuperUser role" : ""}
              >
                SuperUser
              </span>
            </div>
          </div>
          <input
            disabled={updating || !isValid}
            className="btn btn-primary"
            type="submit"
            value="Save"
          />{" "}
          <Link to={`/users/${userId}/permissions`} className="btn btn-link">
            Cancel
          </Link>
        </form>
      )}
    </>
  );
};
